/**
 * FikoToolAxis3DViewer — Takım ekseni tanımı için TAM motor (FikoThreeJsComponent) gömülü 3D triad.
 * Strateji/Makro 3D ile AYNI gömme tekniği (FikoMini3DDialogBase). SVG triad yerine geçer.
 * X/Y/Z oklarına tıkla → o eksen takım ekseni olur · takım sembolüne tıkla → 3D'de yüzeyden seç (parametrik).
 * SADECE sol tuş seçer; orta/sağ tuş kamera kontrolü.
 */
import { Component, ChangeDetectionStrategy, ChangeDetectorRef, Injector, ViewContainerRef, EnvironmentInjector, NgZone, ViewChild, ElementRef, Input, Output, EventEmitter, OnChanges } from '@angular/core';
import { CommonModule } from '@angular/common';
import * as THREE from 'three';
import { FikoCamMini3DBase } from './fiko-cam-mini-3d.base';

@Component({
  selector: 'fiko-toolaxis-3d',
  standalone: true,
  imports: [CommonModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `<div #viewerContainer class="ta3d-vc" [style.height.px]="viewerYukseklik"></div>`,
  styles: [`
    .ta3d-vc{position:relative;width:100%;overflow:hidden;border-radius:6px;border:1px solid rgba(56,189,248,.2);background:#081320;}
    .ta3d-vc ::ng-deep canvas{display:block;width:100%!important;height:100%!important;}
  `]
})
export class FikoToolAxis3DViewer extends FikoCamMini3DBase implements OnChanges {
  @ViewChild('viewerContainer', { static: true }) viewerContainerRef!: ElementRef<HTMLDivElement>;

  @Input() ijk: { x: number; y: number; z: number } = { x: 0, y: 0, z: 1 };
  @Input() bagli = false;             // yüzeye bağlı (parametrik) → takım altın/yeşil
  @Input() aktifEksen: 'x' | 'y' | 'z' | 'custom' | 'yuzey' = 'z';
  @Output() eksenSec = new EventEmitter<'x' | 'y' | 'z'>();   // X/Y/Z oka tıkla
  @Output() yuzeyTikla = new EventEmitter<void>();            // takıma tıkla → yüzeyden seç

  private grup: THREE.Group | null = null;
  private _L = 4;
  private secH: ((e: PointerEvent) => void) | null = null;
  private hovH: ((e: PointerEvent) => void) | null = null;
  private _rc = new THREE.Raycaster();
  private _v2 = new THREE.Vector2();

  constructor(cd: ChangeDetectorRef, injector: Injector, viewContainerRef: ViewContainerRef, environmentInjector: EnvironmentInjector, ngZone: NgZone) {
    super(cd, injector, viewContainerRef, environmentInjector, ngZone);
    this.viewerGenislik = 200; this.viewerYukseklik = 170;
  }

  override sahneHazir(): void {
    if (!this.sahne) return;
    const cv: HTMLCanvasElement | undefined = (this.sahne as any)?.anaGosterim?.canvas?.nativeElement;
    if (cv && !this.secH) {
      this.secH = (e) => this.basla(e); cv.addEventListener('pointerdown', this.secH, true);
      this.hovH = (e) => this.hover(e); cv.addEventListener('pointermove', this.hovH);
    }
    this.ciz();
    setTimeout(() => this.cercevele(), 80);
  }

  ngOnChanges(): void { if (this.hazirMi) { this.ciz(); } }

  private ciz(): void {
    const s: any = this.sahne; if (!s) return;
    if (this.grup) { s.remove(this.grup); this.grup.traverse((o: any) => { o.geometry?.dispose?.(); if (o.material) (Array.isArray(o.material) ? o.material : [o.material]).forEach((m: any) => { m.map?.dispose?.(); m.dispose?.(); }); }); }
    const g = new THREE.Group(); g.name = 'ToolAxisTriad'; this.grup = g;
    const L = this._L;

    // referans zemin grid (XY düzlem)
    const grid = new THREE.GridHelper(L * 2, 8, 0x4a6a8c, 0x223647); grid.rotation.x = Math.PI / 2; g.add(grid);
    // origin küre
    const orj = new THREE.Mesh(new THREE.SphereGeometry(L * 0.07, 16, 14), new THREE.MeshBasicMaterial({ color: 0x22d3ee })); g.add(orj);

    // 3 eksen oku (kalın, tıklanabilir) — X kırmızı, Y yeşil, Z mavi
    this.eksenOku(g, new THREE.Vector3(1, 0, 0), 0xf87171, 'X', 'x');
    this.eksenOku(g, new THREE.Vector3(0, 1, 0), 0x34d399, 'Y', 'y');
    this.eksenOku(g, new THREE.Vector3(0, 0, 1), 0x38bdf8, 'Z', 'z');

    // AKTİF takım sembolü — gerçek IJK yönünde
    const dir = new THREE.Vector3(this.ijk?.x || 0, this.ijk?.y || 0, this.ijk?.z || 1);
    if (dir.lengthSq() < 1e-9) dir.set(0, 0, 1); dir.normalize();
    const tk = new THREE.Group(); tk.name = 'Takim'; tk.userData['kind'] = 'takim';
    const renk = this.bagli ? 0x7fffd4 : 0xe2e8f0;
    // gövde (tutucu)
    const govdeBoy = L * 0.55, govdeR = L * 0.09;
    const govde = new THREE.Mesh(new THREE.CylinderGeometry(govdeR, govdeR, govdeBoy, 18), new THREE.MeshBasicMaterial({ color: renk }));
    govde.position.set(0, L * 1.05 + govdeBoy / 2, 0); govde.userData['kind'] = 'takim'; tk.add(govde);
    // kesici uç (koni, parçaya doğru bakar)
    const uc = new THREE.Mesh(new THREE.ConeGeometry(govdeR * 1.1, L * 0.28, 18), new THREE.MeshBasicMaterial({ color: 0x94a3b8 }));
    uc.position.set(0, L * 1.05 - L * 0.14, 0); uc.rotation.x = Math.PI; uc.userData['kind'] = 'takim'; tk.add(uc);
    // eksen çizgisi (origin → takım)
    const lg = new THREE.BufferGeometry().setFromPoints([new THREE.Vector3(0, 0, 0), new THREE.Vector3(0, L * 1.05, 0)]);
    const ln = new THREE.Line(lg, new THREE.LineDashedMaterial({ color: 0x22d3ee, dashSize: L * 0.12, gapSize: L * 0.08, transparent: true, opacity: .85 }));
    ln.computeLineDistances(); ln.userData['kind'] = 'takim'; tk.add(ln);
    // takımı +Y referansından IJK yönüne döndür
    tk.quaternion.setFromUnitVectors(new THREE.Vector3(0, 1, 0), dir);
    g.add(tk);

    s.add(g);
    if (typeof s.anaGosterim?.ciz === 'function') s.anaGosterim.ciz();
  }

  private eksenOku(g: THREE.Group, dir: THREE.Vector3, renk: number, harf: string, eks: 'x' | 'y' | 'z'): void {
    const L = this._L, aktif = this.aktifEksen === eks;
    const sap = new THREE.Mesh(new THREE.CylinderGeometry(L * 0.025, L * 0.025, L * 0.82, 12), new THREE.MeshBasicMaterial({ color: renk, transparent: true, opacity: aktif ? 1 : .7 }));
    const koni = new THREE.Mesh(new THREE.ConeGeometry(L * 0.07, L * 0.18, 14), new THREE.MeshBasicMaterial({ color: renk, transparent: true, opacity: aktif ? 1 : .7 }));
    const q = new THREE.Quaternion().setFromUnitVectors(new THREE.Vector3(0, 1, 0), dir);
    sap.position.copy(dir.clone().multiplyScalar(L * 0.41)); sap.quaternion.copy(q);
    koni.position.copy(dir.clone().multiplyScalar(L * 0.91)); koni.quaternion.copy(q);
    sap.userData['kind'] = 'eksen'; sap.userData['eksen'] = eks; koni.userData['kind'] = 'eksen'; koni.userData['eksen'] = eks;
    g.add(sap); g.add(koni);
    const lbl = this.harfSprite(harf, renk); lbl.position.copy(dir.clone().multiplyScalar(L * 1.12)); g.add(lbl);
  }

  private harfSprite(metin: string, renk: number): THREE.Sprite {
    const S = 64, cv = document.createElement('canvas'); cv.width = S; cv.height = S; const ctx = cv.getContext('2d')!;
    ctx.fillStyle = '#' + renk.toString(16).padStart(6, '0'); ctx.font = 'bold 48px system-ui,sans-serif'; ctx.textAlign = 'center'; ctx.textBaseline = 'middle'; ctx.fillText(metin, S / 2, S / 2 + 2);
    const tex = new THREE.CanvasTexture(cv); tex.colorSpace = THREE.SRGBColorSpace;
    const sp = new THREE.Sprite(new THREE.SpriteMaterial({ map: tex, depthTest: false, transparent: true }));
    sp.scale.set(this._L * 0.32, this._L * 0.32, 1); sp.renderOrder = 999; return sp;
  }

  private cercevele(): void {
    const ag: any = (this.sahne as any)?.anaGosterim; const kam = ag?.aktifKamera; if (!kam || !this.grup) return;
    const d = this._L * 2.6;
    kam.position.set(d, -d, d * 0.85); if (kam.lookAt) kam.lookAt(0, 0, this._L * 0.4);
    if (kam.isOrthographicCamera) { kam.zoom = Math.max(1.5, 34 / this._L); kam.updateProjectionMatrix?.(); }
    if (ag.aktifKontrol?.target) { ag.aktifKontrol.target.set(0, 0, this._L * 0.4); ag.aktifKontrol.update?.(); }
    ag.ciz?.();
  }

  private isabet(e: MouseEvent): { kind: string; eksen?: string } | null {
    const ag: any = (this.sahne as any)?.anaGosterim; const kam = ag?.aktifKamera; const cv = ag?.canvas?.nativeElement; if (!kam || !cv || !this.grup) return null;
    const r = cv.getBoundingClientRect();
    this._v2.set(((e.clientX - r.left) / r.width) * 2 - 1, -((e.clientY - r.top) / r.height) * 2 + 1);
    this._rc.setFromCamera(this._v2, kam);
    const hit = this._rc.intersectObjects(this.grup.children, true).find(i => (i.object as any).userData?.['kind']);
    if (!hit) return null;
    const ud = (hit.object as any).userData; return { kind: ud['kind'], eksen: ud['eksen'] };
  }

  private basla(e: PointerEvent): void {
    if (e.button !== 0) return;   // SADECE sol tuş seçer
    const h = this.isabet(e); if (!h) return;
    e.stopPropagation(); e.preventDefault();
    if (h.kind === 'eksen' && h.eksen) this.ngZone.run(() => this.eksenSec.emit(h.eksen as 'x' | 'y' | 'z'));
    else if (h.kind === 'takim') this.ngZone.run(() => this.yuzeyTikla.emit());
  }

  private hover(e: MouseEvent): void {
    const ag: any = (this.sahne as any)?.anaGosterim; const cv = ag?.canvas?.nativeElement; if (!cv) return;
    const h = this.isabet(e);
    cv.style.cursor = h ? 'pointer' : 'default';
  }

  protected override temizle(): void {
    const cv: HTMLCanvasElement | undefined = (this.sahne as any)?.anaGosterim?.canvas?.nativeElement;
    if (cv && this.secH) cv.removeEventListener('pointerdown', this.secH, true);
    if (cv && this.hovH) cv.removeEventListener('pointermove', this.hovH);
    this.secH = this.hovH = null;
  }
}
